/**
 * Main application component.
 * Provides the layout structure with sidebar, chart, and dashboard.
 *
 * Phase 3: Chat removed. All runs go through the client-side Web Worker.
 */

import { useState, useEffect, useCallback } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './queryClient';
import { Dashboard } from './components/Dashboard';
import { OptimizerModal } from './components/OptimizerModal';
import { ScannerResults } from './components/ScannerResults';
import { HistoryExplorer } from './components/HistoryExplorer';
import { RunParamsModal } from './components/HistoryExplorer/RunParamsModal';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import { TradesTable } from './components/TradesTable/TradesTable';
import { LogViewer } from './components/LogViewer/LogViewer';
import { AppHeader } from './components/AppHeader/AppHeader';
import { BacktestSidebar } from './components/BacktestSidebar/BacktestSidebar';

import { LoginPage } from './components/LoginPage';
import { RegisterPage } from './components/RegisterPage';
import { ChartSection } from './components/ChartSection/ChartSection';
import { useBacktestStore, useConfigStore } from './stores/backtestStore';
import { useScannerStore } from './stores/scannerStore';
import { useLoadBacktest, useDeleteBacktest } from './hooks/useBacktest';
import { usePerAssetMetrics } from './hooks/usePerAssetMetrics';
import { useMultiAsset } from './hooks/useMultiAsset';
import { useCurrentRunSummary } from './hooks/useCurrentRunSummary';
import { useClientAggregationBacktest } from './hooks/useClientAggregationBacktest';
import type { ApiBacktestResult, SubStrategyConfig, BacktestConfig, Timeframe } from './types';
import { PaperTradingPage } from './components/PaperTradingPage';
import { ConfigurationsPage } from './components/ConfigurationsPage';
import { PortfoliosPage } from './components/PortfoliosPage/PortfoliosPage';
import { OptimizationsPage } from './components/Optimizations/OptimizationsPage';
import { SettingsPage } from './components/SettingsPage';
import { AdminDataCachePage } from './components/AdminDataCachePage';
import { AdminActivityPage } from './components/AdminActivityPage';
import { RunBacktestModal } from './components/RunBacktestModal/RunBacktestModal.js';
import { usePaperTradingStore } from './stores/paperTradingStore';
import { useAuthStore } from './stores/authStore';
import { useUrlSync } from './hooks/useUrlSync';
import { useAuthRouting, legalPageFromPathname, type LegalPage } from './hooks/useAuthRouting';
import { useBacktestDeeplink } from './hooks/useBacktestDeeplink';
import { useAutoLoadSelectedBacktest } from './hooks/useAutoLoadSelectedBacktest';
import { OnboardingFlow } from './components/Onboarding';
import { TermsPage } from './components/TermsPage';
import { PrivacyPage } from './components/PrivacyPage';
import { BacktestingEmptyState } from './components/BacktestingEmptyState';
import { useHasStrategies } from './hooks/useHasStrategies';
import { useSettingsStore } from './stores/settingsStore';
import { shouldRedirectFromGatedPage } from './utils/strategyGate';
import { useOpenedRunsStore } from './stores/openedRunsStore';
import type { BacktestSummary } from './types';

/** BacktestConfig extended with optional aggregation-specific fields stored on the config. */
interface AggregateBacktestConfig extends BacktestConfig {
  allocationMode?: string;
  maxPositions?: number;
}

/** Shown when a deep-linked / sidebar-selected backtest run no longer exists (deleted or stale link). */
function RunNotFoundPanel({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div className="flex-1 flex items-center justify-center p-4">
      <div className="text-center max-w-sm">
        <svg className="w-12 h-12 mx-auto mb-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-gray-300 font-medium">Run not found</p>
        <p className="text-sm text-gray-500 mt-1">
          This backtest run no longer exists — it may have been deleted.
        </p>
        <button
          onClick={onDismiss}
          className="mt-4 px-4 py-2 text-sm rounded-lg bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700 transition-colors"
        >
          Back to runs
        </button>
      </div>
    </div>
  );
}

function AppContent() {
  useUrlSync();
  useBacktestDeeplink();
  const { currentResult, selectedBacktestId, runNotFoundId } = useBacktestStore();
  const { applyHistoryParams } = useConfigStore();
  const { loadBacktest } = useLoadBacktest();
  const deleteBacktestMutation = useDeleteBacktest();
  const { setError } = useBacktestStore();
  const { run: runClientAggregationBacktest } = useClientAggregationBacktest();
  const { scanResults } = useScannerStore();
  const { activePage, setActivePage } = usePaperTradingStore();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const authUser = useAuthStore((s) => s.user);
  const authLogout = useAuthStore((s) => s.logout);
  const [showExplorer, setShowExplorer] = useState(false);
  const [showParamsModal, setShowParamsModal] = useState(false);

  const markOpened = useOpenedRunsStore((s) => s.markOpened);

  const { hasStrategies, isLoading: strategiesLoading } = useHasStrategies();
  const noStrategies = !strategiesLoading && !hasStrategies;
  // The run the user is trying to view was deleted / no longer exists — take
  // priority over the "no strategies" empty state so the not-found message
  // is unambiguous (SHA-31 item 6).
  const showRunNotFound =
    !!runNotFoundId && runNotFoundId === selectedBacktestId && !currentResult;
  const showBacktestingEmptyState = noStrategies && !currentResult && !showRunNotFound;

  const handleDismissRunNotFound = useCallback(() => {
    useBacktestStore.getState().setRunNotFound(null);
    useBacktestStore.getState().setSelectedBacktestId(null);
  }, []);

  // Task 6 — Redirect guard: gated pages redirect to backtesting when no strategies
  useEffect(() => {
    if (shouldRedirectFromGatedPage(activePage, hasStrategies, strategiesLoading)) {
      setActivePage('backtesting');
    }
  }, [activePage, hasStrategies, strategiesLoading, setActivePage]);

  // Mobile master-detail for backtesting page:
  // true = show sidebar (strategy config), false = show main content
  const [backtestShowSidebar, setBacktestShowSidebar] = useState(true);

  const showScanner = scanResults.length > 0;

  const {
    isMultiAsset,
    multiAssets,
    selectedAssetIndex,
    setSelectedAssetIndex,
    selectedAsset,
    assetCandles,
    displayedTrades,
  } = useMultiAsset(currentResult);

  const currentRunSummary = useCurrentRunSummary(currentResult, selectedBacktestId ?? null);
  const dashboardMetrics = usePerAssetMetrics(currentResult, !!isMultiAsset, selectedAsset);

  const handleSelectRun = useCallback(async (id: string) => {
    try {
      const result = await loadBacktest(id);
      if (result) {
        applyHistoryParams(result);
      }
    } catch {
      // loadBacktest already recorded the failure in backtestStore (error +
      // runNotFoundId on 404) — swallow here so a deleted/stale run doesn't
      // surface as an unhandled promise rejection in the click handler.
    }
  }, [loadBacktest, applyHistoryParams]);

  // Auto-load backtest when selectedBacktestId changes — from a direct link,
  // an Explore Runs pick, OR a browser back/forward (popstate) navigation.
  // See useAutoLoadSelectedBacktest for the SHA-40 back-button fix rationale.
  useAutoLoadSelectedBacktest({ selectedBacktestId: selectedBacktestId ?? null, currentResult, handleSelectRun });

  // On mobile: auto-navigate to results when a new backtest result arrives
  useEffect(() => {
    if (currentResult) {
      setBacktestShowSidebar(false);
    }
  }, [currentResult]);

  const handleRerun = async (params: Record<string, unknown>) => {
    // Key on aggregationId instead of symbol==='MULTI' to correctly classify
    // legacy single-strategy runs that had symbol='MULTI' but no aggregationId.
    const isAgg =
      !!currentRunSummary?.aggregationId || !!currentRunSummary?.aggregationName;

    if (isAgg && params.subStrategies) {
      setShowParamsModal(false);
      try {
        const config = currentResult!.config as AggregateBacktestConfig;
        const startDateMs: number = typeof config.startDate === 'number'
          ? config.startDate
          : new Date(config.startDate).getTime();
        const endDateMs: number = typeof config.endDate === 'number'
          ? config.endDate
          : new Date(config.endDate).getTime();
        const exchange = (params.exchange as string) ?? config.exchange ?? 'bybit';

        await runClientAggregationBacktest({
          subStrategies: (params.subStrategies as SubStrategyConfig[]).map((s) => ({
            strategyName: s.strategyName,
            symbol: s.symbol,
            timeframe: s.timeframe as Timeframe,
            params: s.params ?? {},
            exchange: s.exchange ?? exchange,
          })),
          allocationMode: ((params.allocationMode as string) ?? config.allocationMode ?? 'top_n') as 'single_strongest' | 'weighted_multi' | 'top_n',
          maxPositions: (params.maxPositions as number) ?? config.maxPositions ?? 5,
          initialCapital: (params.initialCapital as number) ?? config.initialCapital ?? 10000,
          startDate: new Date(startDateMs).toISOString().slice(0, 10),
          endDate: new Date(endDateMs).toISOString().slice(0, 10),
          exchange,
          aggregationName: currentRunSummary?.aggregationName ?? config.strategyName,
        });
        queryClient.invalidateQueries({ queryKey: ['explorer-history'] });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Aggregation run failed');
      }
    } else {
      // Invariant: this branch is only for single-strategy reruns (isAgg=false).
      // Agg reruns always provide params.subStrategies (even [] from AggregationEditor).
      // Guard: if somehow reached with isAgg=true, symbol='MULTI' from the result config
      // would propagate as the sub-strategy symbol — prevent that silent mis-run.
      if (isAgg) {
        setError(
          'Cannot rerun: sub-strategies are missing from this aggregation result. ' +
          'Re-run from the Aggregations panel instead.',
        );
        setShowParamsModal(false);
        return;
      }
      const { applyHistoryParams: apply } = useConfigStore.getState();
      const updated = {
        ...currentResult!,
        config: { ...currentResult!.config, params },
      } as ApiBacktestResult;
      apply(updated);
      setShowParamsModal(false);
      // Single-strategy re-run: wrap as agg-of-one and run client-side.
      // Thread strategyConfigId from the current run summary so the persisted result
      // links to the correct strategy_config row (MEDIUM-4: prevents minting MULTI configs).
      const cfg = useConfigStore.getState().getConfig();
      try {
        await runClientAggregationBacktest({
          subStrategies: [
            {
              strategyName: cfg.strategyName,
              symbol: cfg.symbol,
              timeframe: cfg.timeframe as Timeframe,
              params: cfg.params,
              exchange: cfg.exchange ?? 'bybit',
            },
          ],
          allocationMode: 'single_strongest',
          maxPositions: 1,
          initialCapital: cfg.initialCapital,
          startDate: typeof cfg.startDate === 'number'
            ? new Date(cfg.startDate).toISOString().slice(0, 10)
            : cfg.startDate,
          endDate: typeof cfg.endDate === 'number'
            ? new Date(cfg.endDate).toISOString().slice(0, 10)
            : cfg.endDate,
          exchange: cfg.exchange ?? 'bybit',
          aggregationName: cfg.strategyName,
          // MEDIUM-4: thread strategyConfigId so the re-run links to the same config
          ...(currentRunSummary?.strategyConfigId != null
            ? { strategyConfigId: currentRunSummary.strategyConfigId }
            : {}),
        });
        queryClient.invalidateQueries({ queryKey: ['explorer-history'] });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Backtest run failed');
      }
    }
  };

  const hasParams =
    !!currentResult?.config.params &&
    Object.keys(currentResult.config.params).length > 0;

  // SHA-43: delete the currently-displayed run from its result page.
  // Reuses the shared useDeleteBacktest hook — same cache invalidation contract
  // (explorer-groups, group-runs, aggregation-runs, strategy-config-runs) as
  // the HistoryExplorer and ConfigRunsTab delete paths.
  const handleDeleteCurrentRun = useCallback(() => {
    if (!currentResult) return;
    deleteBacktestMutation.mutate(currentResult.id, {
      onError: () => {
        alert('Failed to delete this run. Please try again.');
      },
    });
  }, [currentResult, deleteBacktestMutation]);

  return (
    <div className="h-screen app-bg text-gray-100 flex flex-col overflow-hidden">
      {/* Fullscreen onboarding overlay — shown for new users before they've completed onboarding */}
      <OnboardingFlow />

      <AppHeader
        activePage={activePage}
        onNavigate={setActivePage}
        currentResult={currentResult}
        onShowParams={() => setShowParamsModal(true)}
        onShowExplorer={() => setShowExplorer(true)}
        isAuthenticated={isAuthenticated}
        username={authUser?.username}
        onLogout={authLogout}
        hasParams={hasParams}
        hasStrategies={!noStrategies}
      />

      {/* Main Content */}
      {activePage === 'backtesting' && showBacktestingEmptyState && (
        <div className="flex-1 overflow-y-auto">
          <BacktestingEmptyState
            onOpenConnectionGuide={() => {
              useSettingsStore.getState().setActiveTab('apiTokens');
              setActivePage('settings');
            }}
          />
        </div>
      )}

      {activePage === 'backtesting' && !showBacktestingEmptyState && (
        <div className="flex-1 flex overflow-hidden">
          {/* Desktop sidebar — always visible on md+ */}
          <aside
            className="hidden md:block flex-shrink-0 overflow-y-auto"
            style={{
              width: 330,
              borderRight: '1px solid var(--sl-border)',
              background: 'var(--sl-elev)',
            }}
          >
            <ErrorBoundary label="BacktestSidebar">
              <BacktestSidebar
                selectedBacktestId={selectedBacktestId ?? null}
                onSelectRun={async (run: BacktestSummary) => {
                  await handleSelectRun(run.id);
                }}
              />
            </ErrorBoundary>
          </aside>

          {/* Mobile: show sidebar OR main content (master-detail pattern) */}
          <div className="flex md:hidden flex-1 flex-col overflow-hidden">
            {backtestShowSidebar ? (
              /* Mobile sidebar */
              <div
                className="flex-1 overflow-y-auto"
                style={{ background: 'var(--sl-elev)' }}
              >
                <ErrorBoundary label="BacktestSidebar">
                  <BacktestSidebar
                    selectedBacktestId={selectedBacktestId ?? null}
                    onSelectRun={async (run: BacktestSummary) => {
                      await handleSelectRun(run.id);
                    }}
                  />
                </ErrorBoundary>
                {/* View Results button on mobile after a run loads */}
                {currentResult && (
                  <div style={{ padding: '0 12px 16px' }}>
                    <button
                      onClick={() => setBacktestShowSidebar(false)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-500 text-white font-medium text-sm transition-colors"
                    >
                      View Results
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Mobile main content with back button */
              <div className="flex-1 flex flex-col overflow-hidden">
                <div
                  className="flex items-center gap-2 px-3 py-2 shrink-0"
                  style={{ borderBottom: '1px solid var(--sl-border)', background: 'var(--sl-elev)' }}
                >
                  <button
                    onClick={() => setBacktestShowSidebar(true)}
                    className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Runs
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {showRunNotFound ? (
                    <RunNotFoundPanel onDismiss={handleDismissRunNotFound} />
                  ) : (
                    <div className="p-4 space-y-4">
                      <ErrorBoundary label="ChartSection">
                        <ChartSection
                          currentResult={currentResult}
                          isMultiAsset={isMultiAsset}
                          multiAssets={multiAssets}
                          selectedAssetIndex={selectedAssetIndex}
                          onSelectAsset={setSelectedAssetIndex}
                          selectedAsset={selectedAsset}
                          assetCandles={assetCandles}
                          onDeleteRun={handleDeleteCurrentRun}
                        />
                      </ErrorBoundary>

                      {showScanner && (
                        <ErrorBoundary label="ScannerResults">
                          <ScannerResults />
                        </ErrorBoundary>
                      )}

                      <section>
                        <ErrorBoundary label="Dashboard">
                          <Dashboard metrics={dashboardMetrics} />
                        </ErrorBoundary>
                      </section>

                      {currentResult && currentResult.trades.length > 0 && (
                        <section className="bg-gray-800 rounded-lg border border-gray-700 p-4">
                          <ErrorBoundary label="TradesTable">
                            <TradesTable
                              trades={displayedTrades}
                              metrics={currentResult.metrics}
                              showAssetColumn={!!(isMultiAsset && !selectedAsset)}
                              assetLabel={
                                isMultiAsset && selectedAsset
                                  ? selectedAsset.label
                                  : isMultiAsset
                                    ? 'All Assets'
                                    : undefined
                              }
                            />
                          </ErrorBoundary>
                        </section>
                      )}

                      {currentResult && (
                        <section>
                          <ErrorBoundary label="LogViewer">
                            <LogViewer logs={currentResult.strategyLogs ?? []} />
                          </ErrorBoundary>
                        </section>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Desktop main area — always visible on md+ */}
          <main className="hidden md:block flex-1 overflow-y-auto">
            {showRunNotFound ? (
              <RunNotFoundPanel onDismiss={handleDismissRunNotFound} />
            ) : (
              <div className="p-4 space-y-4">
                <ErrorBoundary label="ChartSection">
                  <ChartSection
                    currentResult={currentResult}
                    isMultiAsset={isMultiAsset}
                    multiAssets={multiAssets}
                    selectedAssetIndex={selectedAssetIndex}
                    onSelectAsset={setSelectedAssetIndex}
                    selectedAsset={selectedAsset}
                    assetCandles={assetCandles}
                  />
                </ErrorBoundary>

                {showScanner && (
                  <ErrorBoundary label="ScannerResults">
                    <ScannerResults />
                  </ErrorBoundary>
                )}

                <section>
                  <ErrorBoundary label="Dashboard">
                    <Dashboard metrics={dashboardMetrics} />
                  </ErrorBoundary>
                </section>

                {currentResult && currentResult.trades.length > 0 && (
                  <section className="bg-gray-800 rounded-lg border border-gray-700 p-4">
                    <ErrorBoundary label="TradesTable">
                      <TradesTable
                        trades={displayedTrades}
                        metrics={currentResult.metrics}
                        showAssetColumn={!!(isMultiAsset && !selectedAsset)}
                        assetLabel={
                          isMultiAsset && selectedAsset
                            ? selectedAsset.label
                            : isMultiAsset
                              ? 'All Assets'
                              : undefined
                        }
                      />
                    </ErrorBoundary>
                  </section>
                )}

                {currentResult && (
                  <section>
                    <ErrorBoundary label="LogViewer">
                      <LogViewer logs={currentResult.strategyLogs ?? []} />
                    </ErrorBoundary>
                  </section>
                )}
              </div>
            )}
          </main>
        </div>
      )}

      {activePage === 'configurations' && (
        <ErrorBoundary label="ConfigurationsPage">
          <div className="flex-1 overflow-hidden">
            <ConfigurationsPage />
          </div>
        </ErrorBoundary>
      )}

      {activePage === 'portfolios' && (
        <ErrorBoundary label="PortfoliosPage">
          <div className="flex-1 overflow-hidden flex flex-col">
            <PortfoliosPage />
          </div>
        </ErrorBoundary>
      )}

      {activePage === 'optimizations' && (
        <ErrorBoundary label="OptimizationsPage">
          <div className="flex-1 overflow-hidden flex flex-col">
            <OptimizationsPage />
          </div>
        </ErrorBoundary>
      )}

      {activePage === 'paper-trading' && (
        <ErrorBoundary label="PaperTradingPage">
          <PaperTradingPage />
        </ErrorBoundary>
      )}

      {activePage === 'settings' && (
        <ErrorBoundary label="SettingsPage">
          <div className="flex-1 overflow-y-auto">
            <SettingsPage />
          </div>
        </ErrorBoundary>
      )}

      {activePage === 'admin-data' && (
        <ErrorBoundary label="AdminDataCachePage">
          <div className="flex-1 overflow-hidden flex flex-col">
            <AdminDataCachePage />
          </div>
        </ErrorBoundary>
      )}

      {activePage === 'admin-activity' && (
        <ErrorBoundary label="AdminActivityPage">
          <div className="flex-1 overflow-hidden flex flex-col">
            <AdminActivityPage />
          </div>
        </ErrorBoundary>
      )}

      {activePage === 'terms' && (
        <ErrorBoundary label="TermsPage">
          <div className="flex-1 overflow-y-auto">
            <TermsPage />
          </div>
        </ErrorBoundary>
      )}

      {activePage === 'privacy' && (
        <ErrorBoundary label="PrivacyPage">
          <div className="flex-1 overflow-y-auto">
            <PrivacyPage />
          </div>
        </ErrorBoundary>
      )}

      <HistoryExplorer
        isOpen={showExplorer}
        onClose={() => setShowExplorer(false)}
        onSelectRun={handleSelectRun}
        selectedId={selectedBacktestId}
        onPickRun={(run: BacktestSummary) => {
          markOpened(run);
          void handleSelectRun(run.id);
          setShowExplorer(false);
        }}
      />

      {showParamsModal && currentRunSummary && (
        <RunParamsModal
          run={currentRunSummary}
          isOpen={true}
          onClose={() => setShowParamsModal(false)}
          isRunning={useBacktestStore.getState().isRunning}
          onRerun={handleRerun}
        />
      )}
    </div>
  );
}

function App() {
  const { loadFromStorage, isAuthenticated } = useAuthStore();
  const { authPage, navigateToRegister, navigateToLogin, redirectToApp } = useAuthRouting();

  // SHA-24: /terms and /privacy must be reachable while logged OUT (e.g. a
  // footer link from the login/register screen, or a direct/shared URL).
  // When authenticated, these routes are already handled inside AppContent
  // via activePage + useUrlSync — this state only gates the pre-auth branch.
  const [legalPage, setLegalPage] = useState<LegalPage | null>(() =>
    legalPageFromPathname(window.location.pathname),
  );

  useEffect(() => {
    loadFromStorage();
  }, [loadFromStorage]);

  // Authenticated user lands on /login or /register → redirect them into the app.
  useEffect(() => {
    if (isAuthenticated) {
      const { pathname } = window.location;
      if (pathname === '/login' || pathname === '/register') {
        window.history.replaceState({}, '', '/');
      }
    }
  }, [isAuthenticated]);

  // Keep legalPage in sync with browser back/forward while logged out.
  useEffect(() => {
    function handlePopState(): void {
      setLegalPage(legalPageFromPathname(window.location.pathname));
    }
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleBackToLoginFromLegal = useCallback(() => {
    setLegalPage(null);
    navigateToLogin();
  }, [navigateToLogin]);

  if (!isAuthenticated && legalPage) {
    return (
      <QueryClientProvider client={queryClient}>
        {legalPage === 'terms' ? (
          <TermsPage onBack={handleBackToLoginFromLegal} backLabel="Back to login" />
        ) : (
          <PrivacyPage onBack={handleBackToLoginFromLegal} backLabel="Back to login" />
        )}
      </QueryClientProvider>
    );
  }

  if (!isAuthenticated) {
    return (
      <QueryClientProvider client={queryClient}>
        {authPage === 'login' ? (
          <LoginPage
            onGoToRegister={navigateToRegister}
            onSuccess={redirectToApp}
          />
        ) : (
          <RegisterPage
            onGoToLogin={navigateToLogin}
            onSuccess={redirectToApp}
          />
        )}
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary label="App">
        <OptimizerModal />
        <RunBacktestModal />
        <AppContent />
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
